import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { PerspectiveCamera, Environment, Lightformer } from '@react-three/drei';
import { EffectComposer, N8AO } from '@react-three/postprocessing';
import { Color, Object3D } from 'three';

function Balls({ count = 100, ...props }) {
    const inks = ['#6a00ff', '#ff0095', '#ff0000', '#dadada', '#000000'];
    const rand2hex = () => {
        const rand = Math.random();
        if (rand < 0.15) {
            return new Color(inks[0]);
        } else if (rand < 0.30) {
            return new Color(inks[1]);
        } else if (rand < 0.50) {
            return new Color(inks[2]);
        } else if (rand < 0.99) {
            return new Color(inks[3]);
        }
        return new Color(inks[4]);
    };

    const [colors, setColors] = useState(() => Array.from({ length: count }, rand2hex));
    const [positions, ] = useState(() => Array.from({ length: count }, () => RandomPosition(-5, 5)));
    const [radii, ] = useState(() => Array.from({ length: count }, () => RandomRadius(0.05, 0.4)));

    const meshRef = useRef();
    const tempObject = useMemo(() => new Object3D(), []);

    useFrame(() => {
        if (meshRef.current) {
            positions.forEach((position, i) => {
                tempObject.position.set(...position);
                tempObject.scale.setScalar(radii[i]);
                //tempObject.rotation.x += 0.01;
                //tempObject.rotation.y += 0.01;
                tempObject.updateMatrix();
                meshRef.current.setMatrixAt(i, tempObject.matrix);
                meshRef.current.setColorAt(i, colors[i]);
            });
            meshRef.current.instanceMatrix.needsUpdate = true;
            meshRef.current.instanceColor.needsUpdate = true;
        }
    });

    const handlePointerOver = (event) => {
        const id = event.instanceId;
        const newColors = [...colors];
        newColors[id] = new Color('#00fffb');
        setColors(newColors);
    };

    const handlePointerOut = (event) => {
        const id = event.instanceId;
        const newColors = [...colors];
        newColors[id] = new Color('#00dbd8');
        setColors(newColors);
    };

    return (
        <instancedMesh
            {...props}
            ref={meshRef}
            args={[null, null, count]}
            onPointerOver={handlePointerOver}
            onPointerOut={handlePointerOut}
        >
            <sphereGeometry args={[1, 32, 32]} />
            <meshPhysicalMaterial
                metalness={0.0}
                roughness={0.2}
                reflectivity={0.25}
                clearcoat={1}
                clearcoatRoughness={0.55}
            />
        </instancedMesh>
    );
}

function Camera({ lookAt, ...props }) {
    const cameraRef = useRef();

    useFrame(() => {
        if (cameraRef.current) {
            cameraRef.current.lookAt(lookAt[0], lookAt[1], lookAt[2]);
        }
    });

    return <PerspectiveCamera ref={cameraRef} makeDefault {...props} />;
}

function Group({ mouse, ...props }) {
    const groupRef = useRef();

    const r = useRef({
        brake: 0.0002,
        maxSpeed: 1.9,
        minSpeed: 0.001,
        rotX: 0,
        rotY: 0,
        mouseX: 0,
        mouseY: 0,
        sensetivity: 25
    }).current;

    const adjustSensetivity = (delta) => {
        return delta / r.sensetivity;
    };

    const clampSpeed = (rotation) => {
        return Math.max(Math.min(rotation, r.maxSpeed), -r.maxSpeed);
    }

    const slowdown = (rotation) => {
        return Math.abs(rotation) > r.brake ? rotation - Math.sign(rotation) * r.brake : 0;
    }

    useFrame(() => {
        if (mouse.current) {
            const deltaX = mouse.current.x - r.mouseX;
            const deltaY = mouse.current.y - r.mouseY;

            r.mouseX = mouse.current.x;
            r.mouseY = mouse.current.y;

            r.rotX += adjustSensetivity(deltaY);
            r.rotY += adjustSensetivity(deltaX);

            r.rotX = clampSpeed(r.rotX);
            r.rotY = clampSpeed(r.rotY);
        }
 
        r.rotX = slowdown(r.rotX);
        r.rotY = slowdown(r.rotY); 

        if (Math.abs(r.rotX) < r.minSpeed) {
            r.rotX = r.minSpeed;
        }
        if (Math.abs(r.rotY) < r.minSpeed) {
            r.rotY = r.minSpeed;
        }

        groupRef.current.rotation.x += r.rotX;
        groupRef.current.rotation.y += r.rotY;
        groupRef.current.rotation.z += -r.minSpeed;
    });

    return (
        <group ref={groupRef} {...props}>
            {props.children}
        </group>
    );
}

const RandomRadius = (min, max) => {
    return Math.random() * (max - min) + min;
}

const RandomPosition = (min, max) => {
    return [
        Math.random() * (max - min) + min,
        Math.random() * (max - min) + min,
        Math.random() * (max - min) + min,
    ];
}

function FlyBall({ ...props }) {
    const { className, style } = props;

    const cssClass = className || '';
    const cssStyle = style || { height: '100vh', width: '100vw' };

    const mouse = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const handleMouseMove = (event) => {
            mouse.current.x = (event.clientX / window.innerWidth) * 2 - 1;
            mouse.current.y = -(event.clientY / window.innerHeight) * 2 + 1;
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <Canvas className={cssClass} style={cssStyle} shadows dpr={[1, 1.5]} gl={{ antialias: true }}>
            <fog attach="fog" color="#000000" near={2} far={17} />
            <Camera makeDefault position={[0, 0, 10]} fov={45} lookAt={[0, 0, 0]} />
            <ambientLight intensity={1.75} color={'#ffe4fd'} />
            <spotLight position={[13, 13, 13]} angle={0.3} penumbra={1} intensity={20} castShadow />
            <Group mouse={mouse}>
                <Balls count={200} />
            </Group>
            <EffectComposer disableNormalPass multisampling={8}>
                <N8AO distanceFalloff={1} aoRadius={0.5} intensity={4} />
            </EffectComposer>
            <Environment resolution={32}>
                <group>
                    <Lightformer form="circle" intensity={2.5} position={[0, 5, -9]} scale={4} />
                    <Lightformer form="circle" intensity={5} position={[-5, 1, -1]} scale={1} />
                    <Lightformer form="circle" intensity={10} position={[-5, -1, -1]} scale={2} />
                    <Lightformer form="circle" intensity={5} position={[10, 1, 0]} scale={4} />
                </group>
            </Environment>
        </Canvas>
    );
}

export { FlyBall };